import { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, PointerEvent as ReactPointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import dayjs from 'dayjs';
import ReactECharts from 'echarts-for-react';
import { Empty, Popover, Spin, message as antdMessage } from 'antd';
import {
	ArrowDownOutlined,
	ArrowUpOutlined,
	EyeInvisibleOutlined,
	EyeOutlined,
	LeftOutlined,
	MinusCircleFilled,
	PlusCircleFilled,
} from '@ant-design/icons';
import {
	fetchHomeConfig,
	fetchSeriesSummary,
	fetchStoreRanks,
	fetchTotalSummary,
	saveHomeConfig,
	TYPE,
	type Period,
	type SeriesPoint,
	type StoreRankItem,
	type SummaryDataItem,
	type TotalSummaryItem,
	type TypeCode,
} from '../api/statistic.js';
import { formatNumber, marketCurrency } from '../utils/locale-format.js';
import { mobileColor, mobileFontSize, mobileFontWeight } from '../theme/mobile-typography.js';

const PRIMARY = '#FF7043';
const PALETTE = ['#6DA9D2', '#74BDB6', '#FF7043', '#7C8BEF', '#F4B64A', '#8FCA6B', '#C078D4', '#8E96A3'];
const MAX_DATA_TYPES = 8;
const DEFAULT_APAC_TYPES: TypeCode[] = [TYPE.sjzs, TYPE.dclsjs];

const TYPE_LABEL_KEY: Record<string, string> = {
	'109001': 'sov.t.jzkl',
	'102002': 'sov.t.gdrs',
	'101006': 'sov.t.jdzhl',
	'101008': 'sov.t.xfds',
	'101009': 'sov.t.xsze',
	'101012': 'sov.t.cjl',
	'107001': 'sov.t.sjzs',
	'107002': 'sov.t.dclsjs',
	'101001': 'sov.t.zkl',
	'101005': 'sov.t.jtlsc',
	'101010': 'sov.t.kdj',
	'101011': 'sov.t.ldl',
	'101030': 'sov.t.gdrs_f',
	'111001': 'sov.t.gdrs',
	'111003': 'sov.t.jdzhl',
	'111004': 'sov.t.cjl',
};

const CATALOG_GROUPS: Array<{ titleKey: string; types: string[] }> = [
	{ titleKey: 'sov.cat.business', types: ['101009', '101008', '101010', '101011', '101006', '101012'] },
	{ titleKey: 'sov.cat.traffic', types: ['109001', '102002', '101030', '101001', '101005'] },
	{ titleKey: 'sov.cat.event', types: ['107001', '107002'] },
];

interface Props {
	storeId: string;
	settingsOpen: boolean;
	onSettingsClose: () => void;
}

export default function ApacStoreDataView({ storeId, settingsOpen, onSettingsClose }: Props) {
	const { t, i18n } = useTranslation();
	const [period, setPeriod] = useState<Period>('hour');
	const [types, setTypes] = useState<TypeCode[] | null>(null);
	const [totals, setTotals] = useState<TotalSummaryItem[] | null>(null);
	const [series, setSeries] = useState<SummaryDataItem<SeriesPoint>[] | null>(null);
	const [ranks, setRanks] = useState<Record<string, StoreRankItem> | null>(null);
	const [hiddenInChart, setHiddenInChart] = useState<Set<string>>(new Set());
	const [showAvgFor, setShowAvgFor] = useState<Set<string>>(new Set());

	useEffect(() => {
		let alive = true;
		setTypes(null);
		void fetchHomeConfig()
			.then((config) => {
				if (alive) setTypes(normalizeTypes(config));
			})
			.catch(() => {
				if (alive) setTypes(DEFAULT_APAC_TYPES);
			});
		return () => {
			alive = false;
		};
	}, []);

	useEffect(() => {
		if (!types) return;
		setHiddenInChart(new Set());
		setShowAvgFor(new Set());
	}, [types]);

	useEffect(() => {
		if (!storeId || !types) return;
		let alive = true;
		const current = dateRangeOf(period);
		const previous = lastCycleRangeOf(period);
		setTotals(null);
		void Promise.all([
			fetchTotalSummary({ storeIds: storeId, startDate: current.start, endDate: current.end, types }),
			fetchTotalSummary({ storeIds: storeId, startDate: previous.start, endDate: previous.end, types }),
		])
			.then(([currentItems, previousItems]) => {
				if (!alive) return;
				const previousByType = new Map(previousItems.map((item) => [item.type, item.value]));
				setTotals(currentItems.map((item) => ({
					...item,
					startDate: current.start,
					endDate: current.end,
					lastCycleStartDate: previous.start,
					lastCycleEndDate: previous.end,
					lastCycleValue: previousByType.get(item.type),
				})));
			})
			.catch(() => {
				if (alive) setTotals([]);
			});
		return () => {
			alive = false;
		};
	}, [period, storeId, types]);

	useEffect(() => {
		if (!storeId || !types) return;
		let alive = true;
		const range = chartRangeOf(period);
		setSeries(null);
		void fetchSeriesSummary({
			storeIds: storeId,
			period,
			startDate: range.start,
			endDate: range.end,
			types,
		})
			.then((items) => {
				if (alive) setSeries(items);
			})
			.catch(() => {
				if (alive) setSeries([]);
			});
		return () => {
			alive = false;
		};
	}, [period, storeId, types]);

	useEffect(() => {
		if (!storeId || !types) return;
		let alive = true;
		const range = dateRangeOf(period);
		setRanks(null);
		void fetchStoreRanks({
			storeId,
			startDate: range.start,
			endDate: range.end,
			types,
		})
			.then((items) => {
				if (alive) setRanks(items);
			})
			.catch(() => {
				if (alive) setRanks({});
			});
		return () => {
			alive = false;
		};
	}, [period, storeId, types]);

	const cards = useMemo(() => {
		if (!types) return [];
		const totalByType = new Map((totals ?? []).map((item) => [item.type, item]));
		return types.map((type) => totalByType.get(type) ?? ({ type } as TotalSummaryItem));
	}, [totals, types]);

	const headerDateText = useMemo(() => {
		const current = dateRangeOf(period);
		const label = formatApacDateRange(current.start, current.end);
		if (period === 'hour') return label;
		const previous = lastCycleRangeOf(period);
		return `${label} ${t('native.store.compared_previous_range', { range: formatApacDateRange(previous.start, previous.end) })}`;
	}, [period, t]);

	const chartOption = useMemo(() => {
		if (!types || !series) return null;
		const visibleItems = series.filter((item) => !hiddenInChart.has(item.type));
		if (visibleItems.length === 0) return null;
		const firstPoints = visibleItems[0]?.items ?? [];
		const xAxis = firstPoints.map((point) => pointLabel(point, period));
		const chartSeries = visibleItems.map((item) => {
			const typeIndex = types.indexOf(item.type as TypeCode);
			const color = PALETTE[(typeIndex >= 0 ? typeIndex : 0) % PALETTE.length];
			const values = (item.items ?? []).map((point) => numberOrZero(point.value));
			const avg = values.length > 0 ? values.reduce((sum, value) => sum + value, 0) / values.length : 0;
			return {
				name: labelOfType(item.type, t),
				type: 'line' as const,
				smooth: false,
				showSymbol: false,
				lineStyle: { width: 2, color },
				itemStyle: { color },
				data: values,
				...(showAvgFor.has(item.type) ? {
					markLine: {
						silent: true,
						symbol: 'none',
						lineStyle: { color, type: 'dashed' as const, width: 1.5 },
						label: { formatter: avg.toFixed(2), color, position: 'end' as const },
						data: [{ yAxis: avg, name: t('native.store.average') }],
					},
				} : {}),
			};
		});
		return {
			grid: { left: 8, right: 8, top: 12, bottom: 22, containLabel: true },
			tooltip: { trigger: 'axis' as const },
			xAxis: {
				type: 'category' as const,
				data: xAxis,
				boundaryGap: false,
				axisTick: { show: false },
				axisLine: { lineStyle: { color: '#d9dde3' } },
				axisLabel: { color: '#686f78', fontSize: 11 },
			},
			yAxis: {
				type: 'value' as const,
				show: false,
				splitLine: { lineStyle: { color: '#edf0f3' } },
			},
			series: chartSeries,
		};
	}, [hiddenInChart, period, series, showAvgFor, t, types]);

	const chartLabels = useMemo(() => {
		const first = series?.[0]?.items ?? [];
		if (first.length === 0) return { start: '', end: '' };
		return {
			start: pointLabel(first[0], period, true),
			end: pointLabel(first[first.length - 1], period, true),
		};
	}, [period, series]);

	const toggleHidden = (type: string) => {
		setHiddenInChart((current) => {
			const next = new Set(current);
			if (next.has(type)) next.delete(type);
			else next.add(type);
			return next;
		});
	};

	const toggleAverage = (type: string) => {
		setShowAvgFor((current) => {
			const next = new Set(current);
			if (next.has(type)) next.delete(type);
			else next.add(type);
			return next;
		});
	};

	if (types === null || totals === null) {
		return <div style={{ padding: '44px 0', textAlign: 'center' }}><Spin /></div>;
	}

	return (
		<div style={{ background: '#fff', minHeight: 'calc(100dvh - 58px - env(safe-area-inset-top))', color: '#20242b' }}>
			<div style={{ padding: '18px 14px 12px', color: '#4f5661', fontSize: 15, lineHeight: 1.25 }}>
				{headerDateText}
			</div>

			<div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', rowGap: 18, columnGap: 16, padding: '0 14px 20px' }}>
				{cards.map((card, index) => (
					<ApacMetricSummary
						key={card.type}
						item={card}
						index={index}
						locale={i18n.language}
						label={labelOfType(card.type, t)}
						unit={unitOfType(card, t, i18n.language)}
					/>
				))}
			</div>

			<div style={{ borderTop: '1px solid #edf0f3', padding: '0 14px' }}>
				<div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr)) 38px', alignItems: 'stretch', minHeight: 68 }}>
					{(['hour', 'day', 'week', 'month', 'quarter'] as Period[]).map((item) => (
						<button
							key={item}
							type="button"
							onClick={() => setPeriod(item)}
							style={periodButtonStyle(period === item)}
						>
							{t(`native.store.period_${item}`)}
						</button>
					))}
					<Popover
						trigger="click"
						placement="bottomRight"
						styles={{ body: { padding: 0, borderRadius: 0 } }}
						content={
							<ApacChartVisibilityMenu
								cards={cards}
								hiddenInChart={hiddenInChart}
								showAvgFor={showAvgFor}
								onToggleAverage={toggleAverage}
								onToggleHidden={toggleHidden}
							/>
						}
					>
						<button type="button" aria-label={t('native.store.chart_visibility')} style={eyeButtonStyle}>
							<EyeOutlined />
						</button>
					</Popover>
				</div>
			</div>

			<div style={{ padding: '0 14px 4px' }}>
				<div style={{ display: 'flex', alignItems: 'center', color: '#c4c7cc', fontSize: 18, height: 22 }}>
					<span style={{
						width: 18,
						height: 18,
						borderRadius: '50%',
						display: 'inline-grid',
						placeItems: 'center',
						background: '#d8dbe0',
						color: '#fff',
						fontSize: 13,
						fontWeight: 700,
					}}>
						?
					</span>
				</div>
				<div style={{ height: 330, border: '1px solid #d9dde3', margin: '8px 24px 0', position: 'relative' }}>
					{series === null ? (
						<div style={{ height: '100%', display: 'grid', placeItems: 'center' }}><Spin /></div>
					) : chartOption ? (
						<ReactECharts option={chartOption} style={{ height: '100%', width: '100%' }} opts={{ renderer: 'svg' }} />
					) : (
						<div style={{ height: '100%', display: 'grid', placeItems: 'center' }}>
							<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('native.common.no_data')} />
						</div>
					)}
				</div>
				{chartLabels.start || chartLabels.end ? (
					<div style={{ display: 'flex', justifyContent: 'space-between', color: '#686f78', fontSize: 15, padding: '10px 10px 0' }}>
						<span>{chartLabels.start}</span>
						<span>{chartLabels.end}</span>
					</div>
				) : null}
			</div>

			<div style={{ padding: '28px 14px 28px' }}>
				<div style={{ fontSize: 15, fontWeight: 500, marginBottom: 18 }}>{t('native.store.data_ranking')}</div>
				{ranks === null ? (
					<div style={{ padding: '24px 0', textAlign: 'center' }}><Spin /></div>
				) : (
					<div>
						{cards.map((card, index) => (
							<ApacRankRow
								key={card.type}
								index={index}
								item={card}
								rank={ranks[card.type]}
								label={labelOfType(card.type, t)}
								unit={unitOfType(card, t, i18n.language)}
								locale={i18n.language}
							/>
						))}
					</div>
				)}
			</div>

			<ApacStoreDataSettings
				open={settingsOpen}
				selectedTypes={types}
				cards={cards}
				locale={i18n.language}
				onClose={onSettingsClose}
				onSaved={(nextTypes) => {
					setTypes(nextTypes as TypeCode[]);
					onSettingsClose();
				}}
			/>
		</div>
	);
}

function ApacMetricSummary({ item, index, label, unit, locale }: {
	item: TotalSummaryItem;
	index: number;
	label: string;
	unit: string;
	locale: string;
}) {
	const diff = diffPercent(item.value, item.lastCycleValue);
	return (
		<div style={{ minHeight: 112, display: 'grid', alignContent: 'start' }}>
			<div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#999ea6', fontSize: 13, lineHeight: 1.2, minHeight: 48 }}>
				<span style={{ width: 7, height: 7, borderRadius: '50%', background: PALETTE[index % PALETTE.length], flex: '0 0 auto' }} />
				<span>{label}</span>
			</div>
			<div style={{ marginTop: 12, color: mobileColor.text, fontSize: mobileFontSize.metric, fontWeight: mobileFontWeight.semibold, lineHeight: 1 }}>
				{fmtValue(item.value, item.is_percent, locale)}
				<span style={{ fontSize: 12, fontWeight: 500, marginLeft: 4 }}>{unit}</span>
			</div>
			<div style={{ marginTop: 11, height: 20, color: '#a3a8af', fontSize: 13 }}>
				{diff ? (
					<span style={{ color: diff.up ? '#34c759' : '#ff4d4f', fontSize: mobileFontSize.title }}>
						{diff.up ? <ArrowUpOutlined /> : <ArrowDownOutlined />} {diff.pct}%
					</span>
				) : '--'}
			</div>
		</div>
	);
}

function ApacChartVisibilityMenu({
	cards,
	hiddenInChart,
	showAvgFor,
	onToggleHidden,
	onToggleAverage,
}: {
	cards: TotalSummaryItem[];
	hiddenInChart: Set<string>;
	showAvgFor: Set<string>;
	onToggleHidden: (type: string) => void;
	onToggleAverage: (type: string) => void;
}) {
	const { t } = useTranslation();
	return (
		<div style={{ width: 330, maxWidth: 'calc(100vw - 28px)', background: '#fff', boxShadow: '0 5px 18px rgba(15,23,42,0.15)' }}>
			{cards.map((card) => {
				const hidden = hiddenInChart.has(card.type);
				const showAverage = showAvgFor.has(card.type);
				return (
					<div key={card.type} style={{ display: 'grid', gridTemplateColumns: '1fr 116px', alignItems: 'center', minHeight: 54, borderBottom: '1px solid #edf0f3' }}>
						<button
							type="button"
							onClick={() => onToggleHidden(card.type)}
							style={{
								border: 0,
								background: 'transparent',
								display: 'flex',
								alignItems: 'center',
								gap: 10,
								height: '100%',
								padding: '0 14px',
								textAlign: 'left',
								color: hidden ? '#a8adb5' : '#30343b',
								fontSize: 18,
							}}
						>
							{hidden ? <EyeInvisibleOutlined style={{ color: '#a8adb5' }} /> : <EyeOutlined style={{ color: PRIMARY }} />}
							<span>{labelOfType(card.type, t)}</span>
						</button>
						<button
							type="button"
							onClick={() => onToggleAverage(card.type)}
							style={{
								border: 0,
								background: 'transparent',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'flex-end',
								gap: 11,
								padding: '0 16px 0 6px',
								height: '100%',
								color: '#666b72',
								fontSize: 16,
							}}
						>
							<span>{t('native.store.average')}</span>
							<span style={{
								width: 20,
								height: 20,
								borderRadius: '50%',
								border: showAverage ? '0' : '2px solid #8b929c',
								background: showAverage ? PRIMARY : '#fff',
								color: '#fff',
								display: 'grid',
								placeItems: 'center',
								fontSize: 13,
							}}>
								{showAverage ? '✓' : ''}
							</span>
						</button>
					</div>
				);
			})}
		</div>
	);
}

function ApacRankRow({ item, rank, index, label, unit, locale }: {
	item: TotalSummaryItem;
	rank?: StoreRankItem;
	index: number;
	label: string;
	unit: string;
	locale: string;
}) {
	const { t } = useTranslation();
	const value = fmtValue(rank?.value ?? item.value, item.is_percent, locale);
	const avg = rank?.b_avg ? fmtValue(rank.b_avg, item.is_percent, locale) : '0';
	const exceed = rank?.exceed ? Math.round(Number(rank.exceed) * 100).toString() : '0';
	const top = Number(rank?.top ?? 0);
	return (
		<div style={{ marginBottom: 22 }}>
			<div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#111827' }}>
				<span style={{ width: 7, height: 7, borderRadius: '50%', background: PALETTE[index % PALETTE.length], flex: '0 0 auto' }} />
				<span style={{ fontSize: 15 }}>{label}</span>
				<span style={{ fontSize: mobileFontSize.metric, lineHeight: 1, fontWeight: mobileFontWeight.semibold, marginLeft: 2 }}>{value}</span>
				<span style={{ fontSize: 13 }}>{unit}</span>
				{top > 0 && <RankBadge rank={top} />}
			</div>
			<div style={{ color: '#999ea6', fontSize: 13, lineHeight: 1.35, marginTop: 10, paddingLeft: 18 }}>
				{t('native.store.ranking_detail_with_unit', { average: avg, unit, exceed })}
			</div>
		</div>
	);
}

function RankBadge({ rank }: { rank: number }) {
	const isTop = rank <= 3;
	return (
		<span style={{
			marginLeft: 6,
			minWidth: 28,
			height: 28,
			borderRadius: '50%',
			display: 'inline-grid',
			placeItems: 'center',
			background: isTop ? '#ffd766' : '#c5cad3',
			border: isTop ? '2px solid #ff8f3a' : '0',
			color: isTop ? '#9a5a00' : '#fff',
			fontSize: 15,
			fontWeight: 700,
			boxShadow: isTop ? 'inset 0 0 0 2px rgba(255,255,255,0.45)' : undefined,
		}}>
			{rank}
		</span>
	);
}

function ApacStoreDataSettings({
	open,
	selectedTypes,
	cards,
	locale,
	onClose,
	onSaved,
}: {
	open: boolean;
	selectedTypes: TypeCode[];
	cards: TotalSummaryItem[];
	locale: string;
	onClose: () => void;
	onSaved: (nextTypes: TypeCode[]) => void;
}) {
	const { t } = useTranslation();
	const [draft, setDraft] = useState<TypeCode[]>(selectedTypes);
	const [saving, setSaving] = useState(false);
	const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
	const draggingIndexRef = useRef<number | null>(null);
	const cardByType = useMemo(() => new Map(cards.map((card) => [card.type, card])), [cards]);

	useEffect(() => {
		if (open) {
			setDraft(selectedTypes);
			setDraggingIndex(null);
			draggingIndexRef.current = null;
		}
	}, [open, selectedTypes]);

	useEffect(() => {
		if (draggingIndex === null) return undefined;
		const clearDrag = () => {
			draggingIndexRef.current = null;
			setDraggingIndex(null);
		};
		window.addEventListener('pointerup', clearDrag);
		window.addEventListener('pointercancel', clearDrag);
		return () => {
			window.removeEventListener('pointerup', clearDrag);
			window.removeEventListener('pointercancel', clearDrag);
		};
	}, [draggingIndex]);

	if (!open) return null;

	const draftSet = new Set(draft);
	const candidates = catalogTypes().filter((type) => !draftSet.has(type));
	const remove = (type: TypeCode) => setDraft((current) => current.filter((item) => item !== type));
	const add = (type: TypeCode) => {
		if (draft.length >= MAX_DATA_TYPES) {
			void antdMessage.warning(t('native.store.max_metrics', { count: MAX_DATA_TYPES }));
			return;
		}
		setDraft((current) => current.includes(type) ? current : [...current, type]);
	};
	const moveDraft = (from: number, to: number) => {
		if (from === to || from < 0 || to < 0) return;
		setDraft((current) => {
			if (from >= current.length || to >= current.length) return current;
			const next = [...current];
			const [moved] = next.splice(from, 1);
			next.splice(to, 0, moved);
			return next;
		});
		draggingIndexRef.current = to;
		setDraggingIndex(to);
	};
	const startDrag = (index: number, event: ReactPointerEvent<HTMLDivElement>) => {
		if (event.pointerType === 'mouse' && event.button !== 0) return;
		draggingIndexRef.current = index;
		setDraggingIndex(index);
		event.currentTarget.setPointerCapture?.(event.pointerId);
	};
	const dragMove = (event: ReactPointerEvent<HTMLDivElement>) => {
		const from = draggingIndexRef.current;
		if (from === null) return;
		const target = document.elementFromPoint(event.clientX, event.clientY);
		const item = target instanceof HTMLElement ? target.closest('[data-apac-data-index]') : null;
		const rawIndex = item?.getAttribute('data-apac-data-index');
		if (!rawIndex) return;
		const to = Number(rawIndex);
		if (Number.isInteger(to) && to !== from) moveDraft(from, to);
	};
	const submit = async () => {
		if (saving) return;
		if (draft.length === 0) {
			void antdMessage.warning(t('native.store.keep_one_metric'));
			return;
		}
		setSaving(true);
		try {
			const result = await saveHomeConfig(draft);
			if (result.ok) {
				void antdMessage.success(t('sov.saved'));
				onSaved(draft);
			} else {
				void antdMessage.error(`${t('sov.save_failed')}: ${result.msg ?? ''}`);
			}
		} catch (error) {
			void antdMessage.error(error instanceof Error ? error.message : t('sov.save_failed'));
		} finally {
			setSaving(false);
		}
	};

	return (
		<div style={settingsOverlayStyle}>
			<div style={settingsHeaderStyle}>
				<button type="button" onClick={onClose} style={settingsBackButtonStyle} aria-label={t('native.common.back')}>
					<LeftOutlined />
				</button>
				<div style={{ textAlign: 'center', fontSize: mobileFontSize.topBar, fontWeight: mobileFontWeight.medium }}>{t('native.store.store_data_setting')}</div>
				<button
					type="button"
					onClick={() => void submit()}
					disabled={saving}
					style={{ border: 0, background: 'transparent', color: PRIMARY, fontSize: mobileFontSize.title, paddingRight: 14, opacity: saving ? 0.55 : 1 }}
				>
					{t('native.common.complete')}
				</button>
			</div>

			<div style={{ background: '#f5f6f8', color: '#707780', fontSize: 14, lineHeight: 1.3, padding: '26px 20px 22px' }}>
				{t('native.store.data_selected_hint')}
			</div>
			<div
				style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', background: '#fff', borderTop: '1px solid #edf0f3', borderBottom: '1px solid #edf0f3' }}
				onPointerMove={dragMove}
			>
				{draft.map((type, index) => {
					const card = cardByType.get(type) ?? ({ type } as TotalSummaryItem);
					const diff = diffPercent(card.value, card.lastCycleValue);
					return (
						<div
							key={type}
							data-apac-data-index={index}
							onPointerDown={(event) => startDrag(index, event)}
							style={{
								minHeight: 170,
								borderRight: '1px solid #edf0f3',
								padding: '22px 30px 16px 18px',
								position: 'relative',
								touchAction: 'none',
								background: draggingIndex === index ? '#fff7f2' : '#fff',
							}}
						>
							<div style={{ color: '#9aa0a6', fontSize: 14, lineHeight: 1.22 }}>{labelOfType(type, t)}</div>
							<button
								type="button"
								aria-label={`${t('native.common.remove_picture')} ${labelOfType(type, t)}`}
								onPointerDown={(event) => event.stopPropagation()}
								onClick={(event) => {
									event.stopPropagation();
									remove(type);
								}}
								style={{ position: 'absolute', top: 18, right: 18, border: 0, padding: 0, background: 'transparent', color: '#f05252', fontSize: 25 }}
							>
								<MinusCircleFilled />
							</button>
							<div style={{ marginTop: 28, color: mobileColor.text, fontSize: mobileFontSize.metric, lineHeight: 1, fontWeight: mobileFontWeight.semibold }}>
								{fmtValue(card.value, card.is_percent, locale)}
							</div>
							<div style={{ marginTop: 10, color: '#a3a8af', fontSize: 13 }}>
								{diff ? (
									<span style={{ color: diff.up ? '#34c759' : '#ff4d4f' }}>
										{diff.up ? <ArrowUpOutlined /> : <ArrowDownOutlined />} {diff.pct}%
									</span>
								) : '--'}
							</div>
						</div>
					);
				})}
				{draft.length === 0 && (
					<div style={{ gridColumn: '1 / -1', minHeight: 120, display: 'grid', placeItems: 'center', color: '#9aa0a6', fontSize: mobileFontSize.body }}>
						{t('native.store.keep_one_metric')}
					</div>
				)}
			</div>

			<div style={{ background: '#f5f6f8', color: '#707780', fontSize: 14, padding: '28px 20px 20px' }}>
				{t('native.store.manage_data')}
			</div>
			<div style={{ background: '#fff' }}>
				{candidates.length === 0 ? (
					<Empty description={t('native.common.no_data')} style={{ padding: '42px 0' }} />
				) : (
					candidates.map((type) => (
						<button
							key={type}
							type="button"
							onClick={() => add(type)}
							style={{
								width: '100%',
								minHeight: 62,
								border: 0,
								borderBottom: '1px solid #edf0f3',
								background: '#fff',
								display: 'flex',
								alignItems: 'center',
								gap: 14,
								padding: '0 20px',
								color: '#707780',
								fontSize: 14,
								textAlign: 'left',
							}}
						>
							<PlusCircleFilled style={{ color: '#38c64a', fontSize: 31, flex: '0 0 auto' }} />
							<span>{labelOfType(type, t)}</span>
						</button>
					))
				)}
			</div>
		</div>
	);
}

function normalizeTypes(config: TypeCode[] | null): TypeCode[] {
	if (!config || config.length === 0) return DEFAULT_APAC_TYPES;
	const valid = new Set(catalogTypes());
	const next: TypeCode[] = [];
	for (const type of config) {
		if (!valid.has(type) || next.includes(type)) continue;
		next.push(type);
		if (next.length >= MAX_DATA_TYPES) break;
	}
	return next.length > 0 ? next : DEFAULT_APAC_TYPES;
}

function catalogTypes(): TypeCode[] {
	const seen = new Set<string>();
	const result: TypeCode[] = [];
	for (const group of CATALOG_GROUPS) {
		for (const type of group.types) {
			if (seen.has(type)) continue;
			seen.add(type);
			result.push(type as TypeCode);
		}
	}
	return result;
}

function ymd(date: Date) {
	return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function today() {
	return ymd(new Date());
}

function dateRangeOf(period: Period): { start: string; end: string } {
	const end = today();
	const date = new Date();
	switch (period) {
		case 'hour':
		case 'day':
			return { start: end, end };
		case 'week': {
			const dayIndex = (date.getDay() + 6) % 7;
			date.setDate(date.getDate() - dayIndex);
			return { start: ymd(date), end };
		}
		case 'month':
			date.setDate(1);
			return { start: ymd(date), end };
		case 'quarter': {
			const quarterMonth = Math.floor(date.getMonth() / 3) * 3;
			date.setMonth(quarterMonth, 1);
			return { start: ymd(date), end };
		}
	}
}

function lastCycleRangeOf(period: Period): { start: string; end: string } {
	const current = dateRangeOf(period);
	const start = new Date(current.start);
	if (period === 'hour' || period === 'day') {
		start.setDate(start.getDate() - 1);
		return { start: ymd(start), end: ymd(start) };
	}
	if (period === 'week') {
		const end = new Date(start);
		start.setDate(start.getDate() - 7);
		end.setDate(end.getDate() - 1);
		return { start: ymd(start), end: ymd(end) };
	}
	if (period === 'month') {
		const end = new Date(start);
		start.setMonth(start.getMonth() - 1);
		end.setDate(end.getDate() - 1);
		return { start: ymd(start), end: ymd(end) };
	}
	const end = new Date(start);
	start.setMonth(start.getMonth() - 3);
	end.setDate(end.getDate() - 1);
	return { start: ymd(start), end: ymd(end) };
}

function chartRangeOf(period: Period): { start: string; end: string } {
	const end = today();
	const date = new Date();
	if (period === 'hour') return { start: end, end };
	if (period === 'day') date.setDate(date.getDate() - 6);
	if (period === 'week') date.setDate(date.getDate() - 35);
	if (period === 'month') date.setMonth(date.getMonth() - 5);
	if (period === 'quarter') date.setMonth(date.getMonth() - 18);
	return { start: ymd(date), end };
}

function pointLabel(point: SeriesPoint | undefined, period: Period, full = false) {
	if (!point) return '';
	if (point.hour != null) return full ? `${point.hour}:00` : `${point.hour}`;
	if (point.date) return full ? formatApacDate(point.date) : formatApacDate(point.date);
	if (period === 'week' && point.year && point.week) return full ? `Week ${point.week},${point.year}` : `Week ${point.week}`;
	if (period === 'month' && point.year && point.month) return `${point.month}/${point.year}`;
	if (period === 'quarter' && point.year && point.quarter) return `Q${point.quarter},${point.year}`;
	if (point.start_date) return full ? formatApacDate(point.start_date) : formatApacDate(point.start_date);
	return '';
}

function formatApacDate(value: string) {
	const parsed = dayjs(value);
	return parsed.isValid() ? parsed.format('DD/MM/YYYY') : value;
}

function formatApacDateRange(start: string, end: string) {
	if (start === end) return formatApacDate(start);
	return `${formatApacDate(start)}to${formatApacDate(end)}`;
}

function labelOfType(type: string, t: TFunction) {
	const key = TYPE_LABEL_KEY[type];
	return key ? t(key) : type;
}

function numberOrZero(value?: string | number) {
	const parsed = Number(value ?? 0);
	return Number.isFinite(parsed) ? parsed : 0;
}

function fmtValue(value: string | undefined, isPercent: string | undefined, locale: string) {
	if (isPercent === '1') {
		const parsed = numberOrZero(value) * 100;
		return parsed % 1 === 0 ? formatNumber(parsed, locale) : parsed.toFixed(2);
	}
	const parsed = Number(value ?? 0);
	if (!Number.isFinite(parsed)) return value ?? '0';
	return parsed % 1 === 0 ? formatNumber(parsed, locale) : parsed.toFixed(2);
}

function unitOfType(item: TotalSummaryItem, t: TFunction, locale: string) {
	if (item.is_percent === '1') return '%';
	if (item.type === TYPE.xsze || item.type === TYPE.kdj) return marketCurrency(locale);
	if (item.type === TYPE.xfds || item.type === TYPE.sjzs || item.type === TYPE.dclsjs) return t('native.common.pcs');
	return t('native.common.people');
}

function diffPercent(current?: string, previous?: string): { up: boolean; pct: string } | null {
	const previousNumber = Number(previous);
	if (!Number.isFinite(previousNumber) || previousNumber <= 0) return null;
	const currentNumber = Number(current ?? 0);
	if (!Number.isFinite(currentNumber)) return null;
	const diff = ((currentNumber - previousNumber) * 100) / previousNumber;
	return { up: diff >= 0, pct: Math.abs(diff).toFixed(2) };
}

const periodButtonStyle = (active: boolean): CSSProperties => ({
	border: 0,
	borderBottom: active ? `3px solid ${PRIMARY}` : '3px solid transparent',
	background: 'transparent',
	color: active ? PRIMARY : '#5e636b',
	fontSize: mobileFontSize.topBarLarge,
	fontWeight: active ? mobileFontWeight.medium : mobileFontWeight.regular,
	padding: '12px 0 8px',
	lineHeight: 1.2,
});

const eyeButtonStyle: CSSProperties = {
	border: 0,
	borderBottom: '3px solid transparent',
	background: 'transparent',
	color: PRIMARY,
	fontSize: 27,
	padding: 0,
	display: 'grid',
	placeItems: 'center',
};

const settingsOverlayStyle: CSSProperties = {
	position: 'fixed',
	zIndex: 1350,
	top: 0,
	right: 0,
	bottom: 0,
	left: 0,
	background: '#fff',
	color: '#20242b',
	overflowY: 'auto',
};

const settingsHeaderStyle: CSSProperties = {
	height: 'calc(52px + env(safe-area-inset-top))',
	paddingTop: 'env(safe-area-inset-top)',
	display: 'grid',
	gridTemplateColumns: '72px 1fr 112px',
	alignItems: 'center',
	background: '#fff',
	borderBottom: '1px solid #edf0f3',
};

const settingsBackButtonStyle: CSSProperties = {
	border: 0,
	background: 'transparent',
	color: PRIMARY,
	fontSize: 22,
	height: 52,
	textAlign: 'left',
	paddingLeft: 14,
};
